/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { jsx, cssMap as unboundedCssMap } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { useBackgroundColorWithHoverState, useDarkModeColors } from '@atlassian/color-picker';

import { useExtensionsState } from '../../../controllers/use-extensions-state';
import { useIsHubCornerRadiusEnabled } from '../../../controllers/use-is-hub-corner-radius-enabled';
import { useProductDependencies } from '../../../controllers/use-product-dependencies';
import { useShouldShowPremiumUpsell } from '../../../controllers/use-should-show-premium-upsell';
import { CornerStyle } from '../../../types';
import {
	HUB_CORNER_RADIUS_CSS_VAR,
	resolveHubCornerRadius,
} from '../../../utils/hub-corner-radius';

import messages from './messages';
import type { CardContainerProps } from './types';

const unboundedStyles = unboundedCssMap({
	containerBase: {
		boxSizing: 'border-box',
		overflow: 'hidden',
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		marginTop: token('space.025'),
		marginRight: token('space.025'),
		marginBottom: token('space.025'),
		marginLeft: token('space.025'),

		// dynamics styles' defaults
		height: '100%',
		color: token('color.text'),
		backgroundColor: token('elevation.surface.overlay'),
	},
	containerBaseUpdated: {
		boxSizing: 'border-box',
		overflow: 'hidden',
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),

		// dynamics styles' defaults
		height: '100%',
		color: token('color.text'),
		backgroundColor: token('elevation.surface.overlay'),
	},
	containerCorner: {
		// for user set corner
		borderRadius: token('radius.large'),
	},
	viewOnHover: {
		'&:hover': {
			backgroundColor: token('elevation.surface.overlay.hovered'),
			cursor: 'pointer',
		},
	},
	overlayButtonForSelectToEdit: {
		position: 'absolute',
		border: 'none',
		pointEvents: 'none',
		backgroundColor: 'transparent',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 1,
	},
	selectedToEdit: {
		outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
	},
	selectedToRemove: {
		outline: `${token('border.width.focused')} solid ${token('color.border.danger')}`,

		'&::before': {
			content: '""',
			position: 'absolute',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			zIndex: 1,
			opacity: 0.5,
			backgroundColor: token('color.background.danger'),
		},
	},
});

type EditState = {
	isSelectedToEdit: boolean;
	isSelectedToRemove: boolean;
};

const SelectToEditButton = ({
	cardId,
	extensionLocalId,
	onSelectStateUpdate,
}: {
	cardId?: string;
	extensionLocalId?: string;
	onSelectStateUpdate?: (state: EditState) => void;
}) => {
	const [
		{ selectedExtensionLocalId, selectedToEditCardId, selectedToRemoveCardId },
		{ setIsInAutoFocusMode, setSelectedToEditCardId },
	] = useExtensionsState();

	const handleClick = useCallback(() => {
		if (cardId && extensionLocalId === selectedExtensionLocalId) {
			setIsInAutoFocusMode(true);
			setSelectedToEditCardId(cardId);
		}
	}, [
		cardId,
		extensionLocalId,
		selectedExtensionLocalId,
		setSelectedToEditCardId,
		setIsInAutoFocusMode,
	]);

	const isSelectedToEdit =
		selectedToEditCardId === cardId && extensionLocalId === selectedExtensionLocalId;
	const isSelectedToRemove =
		selectedToRemoveCardId === cardId && extensionLocalId === selectedExtensionLocalId;

	useEffect(() => {
		onSelectStateUpdate?.({ isSelectedToEdit, isSelectedToRemove });
	}, [isSelectedToEdit, isSelectedToRemove, onSelectStateUpdate]);

	/*
		Overlay an invisible button to allow click interaction on edit.
		Also avoid violating A11y rules for making non-interactive element interactive by putting onClick on the container.
	*/

	// eslint-disable-next-line @atlaskit/design-system/no-html-button
	return (
		<button css={unboundedStyles.overlayButtonForSelectToEdit} onClick={handleClick}>
			<VisuallyHidden>
				<FormattedMessage {...messages.selectToEditAriaLabel} />
			</VisuallyHidden>
		</button>
	);
};

/**
 *
 * This component is used to wrap the card content and set the followings of the card
 * - border
 * - height (optional)
 * - background color
 * - hover effect ( for view)
 * - outline (for edit)
 * - color
 */
export const CardContainer = ({
	id,
	cardHeight,
	isInViewMode,
	children,
	hasLink,
	cardId,
	extensionLocalId,
	cornerStyle,
	backgroundColor: savedBackgroundColor,
	textColor: savedTextColor,
	borderColor,
	isEditable,
	isHoverable = true,
	enableHubCornerRadius = false,
	cornerRadiusOverride,
}: CardContainerProps): JSX.Element => {
	const isCardEditable = !isInViewMode && isEditable;
	const isCardHoverable = isInViewMode && hasLink && isHoverable;

	const [isSelectedToEdit, setIsSelectedToEdit] = useState(false);
	const [isSelectedToRemove, setIsSelectedToRemove] = useState(false);

	// handle theme responsiveness for text and background colors
	const { isSSRProductInDarkMode } = useProductDependencies();
	const { getDarkModeCompatibleTextColor, getDarkModeCompatibleBackgroundColor } =
		useDarkModeColors(isSSRProductInDarkMode);
	const textColor = useMemo(
		() => (savedTextColor ? getDarkModeCompatibleTextColor(savedTextColor) : undefined),
		[savedTextColor, getDarkModeCompatibleTextColor],
	);
	const backgroundColor = useMemo(
		() =>
			savedBackgroundColor ? getDarkModeCompatibleBackgroundColor(savedBackgroundColor) : undefined,
		[savedBackgroundColor, getDarkModeCompatibleBackgroundColor],
	);

	const {
		backgroundColor: currentBackgroundColor,
		handleHover,
		handleUnhover,
	} = useBackgroundColorWithHoverState(backgroundColor);

	const handleMouseEnter = useCallback(() => {
		handleHover();
	}, [handleHover]);

	const handleMouseLeave = useCallback(() => {
		handleUnhover();
	}, [handleUnhover]);

	// handle select to edit
	const handleSelectStateUpdate = useCallback(
		({ isSelectedToEdit, isSelectedToRemove }: EditState) => {
			setIsSelectedToEdit(isSelectedToEdit);
			setIsSelectedToRemove(isSelectedToRemove);
		},
		[setIsSelectedToEdit, setIsSelectedToRemove],
	);

	const hideCorner = cornerStyle === CornerStyle.SQUARED;

	const isHubCornerRadiusEnabled = useIsHubCornerRadiusEnabled();
	const useHubRadius = enableHubCornerRadius && isHubCornerRadiusEnabled;
	// Per-element corner radius is Premium. The value lives in the ADF, so a
	// downgrade cannot clear it — ignoring the override here makes the element
	// inherit the hub-wide radius, which the backend resets to the 8px default.
	const isCornerRadiusLocked = useShouldShowPremiumUpsell();

	return (
		<div
			data-testid={id}
			css={[
				fg('enable_jsm_help_extension_redesign')
					? unboundedStyles.containerBaseUpdated
					: unboundedStyles.containerBase,
				!useHubRadius && !hideCorner && unboundedStyles.containerCorner,
				isCardHoverable && unboundedStyles.viewOnHover,
				isSelectedToEdit && unboundedStyles.selectedToEdit,
				isSelectedToRemove && unboundedStyles.selectedToRemove,
			]}
			style={{
				height: cardHeight,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				color: textColor,
				backgroundColor: currentBackgroundColor,
				borderColor,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values
				...(useHubRadius
					? {
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
							borderRadius: `var(${HUB_CORNER_RADIUS_CSS_VAR})`,
							...(!isCornerRadiusLocked && typeof cornerRadiusOverride === 'number'
								? {
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
										[HUB_CORNER_RADIUS_CSS_VAR]: `${resolveHubCornerRadius(cornerRadiusOverride)}px`,
									}
								: {}),
						}
					: {}),
			}}
			// if savedBackgroundColor is not set, use default hover effect
			{...(isCardHoverable && !!savedBackgroundColor
				? {
						onMouseEnter: handleMouseEnter,
						onMouseLeave: handleMouseLeave,
					}
				: {})}
		>
			{isCardEditable && (
				<SelectToEditButton
					onSelectStateUpdate={handleSelectStateUpdate}
					cardId={cardId}
					extensionLocalId={extensionLocalId}
				/>
			)}

			{children}
		</div>
	);
};
